import { Check, Copy, ScrollText, ShieldCheck, ShieldOff } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { SECURITY_RULE_IDS, SECURITY_RULE_LABEL } from '@orgus/protocol';
import type { DetectionSettings, SecurityAlert, SecurityRuleId, SecurityState } from '@orgus/protocol';
import {
  Button,
  ConfirmDialog,
  Disclosure,
  Drawer,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  MeterBar,
  Panel,
  RingMeter,
  StatStrip,
  StatTile,
  formatRelative,
  Pressable,
  RelativeTime,
  RowList,
  RowStatus,
  Segmented,
  Select,
  Skeleton,
  SkeletonRows,
  Switch,
  cn,
  formatAbsolute,
  formatBytes,
  formatDate,
  formatInt,
  plural,
  toast,
} from '@orgus/ui';
import type { RowStatusProps } from '@orgus/ui';
import { useOrgus, useMachines } from '../../lib/store.ts';
import { StatStripSkeleton } from '../fleet/StatStrip.tsx';
import { PageVerdict } from '../glance/PageVerdict.tsx';
import type { Verdict } from '../glance/PageVerdict.tsx';
import { DetailSection, FactGrid } from '../detail/FactGrid.tsx';
import { revokeMachine } from '../machines/revoke.ts';
import { KillSwitches } from '../trust/TrustSettings.tsx';
import { useTrust } from '../trust/useTrust.ts';
import type { TrustApi } from '../trust/useTrust.ts';
import { machineLabel, restoreSummary } from './model.ts';
import { useSecurity } from './useSecurity.ts';
import type { SecurityApi } from './useSecurity.ts';

const WEEK = 7 * 86_400_000;

/** What happens when a machine is revoked, in one place so the confirmation and the row agree. */
export function revokeConsequence(name: string): string {
  return `${name} is disconnected at once and needs a new join token to return; work running there keeps going, out of Orgus's reach.`;
}

/** An alert's one status: critical and warning need a person; acknowledged is grey. */
export function alertRowStatus(a: SecurityAlert): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  if (a.status === 'acknowledged') return { tone: 'neutral', icon: 'done', label: 'Acknowledged' };
  return a.severity === 'critical' ? { tone: 'danger', icon: 'failed', label: 'Critical' } : { tone: 'attention', icon: 'warning', label: 'Warning' };
}

function SecuritySkeleton() {
  return (
    <div className="flex flex-col gap-6" role="status" aria-busy="true" aria-label="Loading security">
      <StatStripSkeleton cells={4} />
      <div className="rounded-card border border-border bg-surface">
        <div className="flex h-10 items-center border-b border-border-subtle px-4">
          <Skeleton className="h-3 w-24" />
        </div>
        <SkeletonRows rows={3} />
      </div>
      <div className="rounded-card border border-border bg-surface">
        <SkeletonRows rows={3} />
      </div>
    </div>
  );
}

/**
 * Security: what needs a person first (open alerts), then the emergency controls (freeze,
 * pause a project, revoke a machine), the detection rules, and backups.
 */
export function SecurityView() {
  const sec = useSecurity();
  const trust = useTrust();
  const [params] = useSearchParams();
  const focus = params.get('alert');

  if (sec.loading && !sec.state) return <SecuritySkeleton />;
  if (!sec.state) return <ErrorState error={sec.error ?? new Error('The hub sent no security state.')} onRetry={sec.refresh} />;
  return (
    <div className="flex flex-col gap-6">
      {sec.error != null && <ErrorState variant="inline" error={sec.error} onRetry={sec.refresh} />}
      <Summary s={sec.state} trust={trust} />
      {sec.state.alerts.length > 0 && <Alerts sec={sec} s={sec.state} focus={focus} />}
      <EmergencyControls t={trust} />
      <Rules sec={sec} s={sec.state} />
      <Backups s={sec.state} />
      {sec.fetchedAt && (
        <p className="text-caption text-fg-subtle">
          Updated <RelativeTime ts={sec.fetchedAt} />. Checks again every 10 seconds.
        </p>
      )}
    </div>
  );
}

/** The page's one answer: is everything safe, and what needs a person. */
export function securityVerdict(s: SecurityState, frozen: string | null, now = Date.now()): Verdict {
  const open = s.alerts.filter((a) => a.status === 'open');
  const critical = open.filter((a) => a.severity === 'critical').length;
  const b = s.lastBackup;
  const stale = !b || now - b.at > WEEK;
  const behind = Object.entries(s.format.known).some(([k, v]) => (s.format.versions[k] ?? 0) < v);
  if (critical) return { tone: 'danger', title: `${plural(critical, 'critical alert')} to review`, line: open[0]?.title };
  if (frozen) return { tone: 'danger', title: 'Writes are frozen', line: `By ${frozen}. Lift the freeze in Emergency controls when it is safe.` };
  if (open.length) return { tone: 'attention', title: `${plural(open.length, 'alert')} to review`, line: open[0]?.title };
  if (stale) return { tone: 'attention', title: 'Everything is safe, but back up the hub', line: b ? 'The last backup is over a week old.' : 'No backup yet. Run orgus backup.' };
  if (behind) return { tone: 'attention', title: 'Everything is safe', line: 'The hub folder format is behind; it migrates on the next start.' };
  return { tone: 'ok', title: 'Everything is safe', line: 'No open alerts, writes allowed, and a recent backup.' };
}

/** The answer, then four shapes: alerts, writes, detection and backup. Grey unless a person must act. */
function Summary({ s, trust }: { s: SecurityState; trust: TrustApi }) {
  const open = s.alerts.filter((a) => a.status === 'open');
  const critical = open.filter((a) => a.severity === 'critical').length;
  const b = s.lastBackup;
  const now = Date.now();
  const age = b ? now - b.at : Infinity;
  const stale = age > WEEK;
  const t = trust.state;
  const on = SECURITY_RULE_IDS.filter((id) => s.rules[id].enabled).length;
  const v = securityVerdict(s, t?.frozen?.by ?? null, now);
  const toAlerts = () => document.getElementById('security-alerts')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  return (
    <div className="flex flex-col gap-4">
      <PageVerdict
        v={v}
        action={
          open.length ? (
            <Button variant="primary" size="sm" onClick={toAlerts}>
              Review alerts
            </Button>
          ) : undefined
        }
      />
      <StatStrip label="Security now" className="lg:grid-cols-4">
        <StatTile
          label="Open alerts"
          value={open.length}
          attention={open.length > 0}
          quietAtZero
          sub={critical ? `${formatInt(critical)} critical` : open.length ? 'Warnings only' : 'Nothing to review'}
          visual={s.alerts.length ? <MeterBar value={s.alerts.length ? 1 - open.length / s.alerts.length : 1} label={`${formatInt(s.alerts.length - open.length)} of ${formatInt(s.alerts.length)} alerts acknowledged`} /> : undefined}
          onOpen={open.length ? toAlerts : undefined}
        />
        <StatTile
          label="Writes"
          value={t?.frozen ? 0 : 1}
          format={() => (!t ? 'Unknown' : t.frozen ? 'Frozen' : 'Allowed')}
          attention={!!t?.frozen}
          loading={trust.loading && !t}
          sub={!t ? 'Did not load' : t.frozen ? `By ${t.frozen.by}` : t.pausedProjects.length ? `${plural(t.pausedProjects.length, 'project')} paused` : 'Every project takes work'}
        />
        <StatTile
          label="Detection rules on"
          value={on}
          of={SECURITY_RULE_IDS.length}
          sub={on === SECURITY_RULE_IDS.length ? 'Watching everything' : `${formatInt(SECURITY_RULE_IDS.length - on)} off`}
          trend={<RingMeter value={on / SECURITY_RULE_IDS.length} size={40} tone={3} label={`${formatInt(on)} of ${formatInt(SECURITY_RULE_IDS.length)} detection rules on`} />}
          onOpen={() => document.getElementById('security-rules')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
        />
        <StatTile
          label="Last backup"
          value={b ? 1 : 0}
          format={() => (b ? formatRelative(b.at, now).replace(/\s*ago$/, '') : 'None yet')}
          unit={b ? 'ago' : undefined}
          attention={stale}
          sub={stale || !b ? 'Run orgus backup' : `${formatInt(b.files)} files, ${formatBytes(b.bytes)}`}
          trend={<RingMeter value={b ? Math.max(0, 1 - age / WEEK) : 0} size={40} tone={stale ? 'attention' : 3} label={b ? 'Backup freshness this week' : 'No backup yet'} />}
          onOpen={() => document.getElementById('security-backups')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
        />
      </StatStrip>
    </div>
  );
}

function Alerts({ sec, s, focus }: { sec: SecurityApi; s: SecurityState; focus: string | null }) {
  const machines = useOrgus((st) => st.machines);
  const navigate = useNavigate();
  const [view, setView] = useState<'open' | 'all'>('open');
  const [openId, setOpenId] = useState<string | null>(focus);
  const [ack, setAck] = useState<SecurityAlert | null>(null);
  const [note, setNote] = useState('');
  const [revoke, setRevoke] = useState<{ id: string; name: string } | null>(null);
  const open = s.alerts.filter((a) => a.status === 'open');
  const critical = open.filter((a) => a.severity === 'critical').length;
  const rows = view === 'open' ? open : s.alerts;
  const shown = s.alerts.find((a) => a.id === openId);

  useEffect(() => {
    if (!focus) return;
    if (s.alerts.some((a) => a.id === focus && a.status !== 'open')) setView('all');
    setOpenId(focus);
  }, [focus, s.alerts]);

  const auditLink = (a: SecurityAlert) => (a.actor ? `/audit?actor=${encodeURIComponent(a.actor)}` : a.machineId ? `/audit?machine=${encodeURIComponent(a.machineId)}` : '/audit');
  const revokable = (a: SecurityAlert) => {
    const m = a.machineId ? machines[a.machineId] : undefined;
    return m && a.status === 'open' && (a.rule === 'credential-two-places' || a.rule === 'new-address') ? m : undefined;
  };
  const startAck = (a: SecurityAlert) => {
    setNote('');
    setAck(a);
  };

  return (
    <Panel
      id="security-alerts"
      title="Alerts"
      sub={open.length ? `${formatInt(open.length)} open${critical ? `, ${formatInt(critical)} critical` : ''}` : undefined}
      actions={
        <Segmented
          label="Which alerts"
          value={view}
          onChange={setView}
          options={[
            { value: 'open', label: 'Open', count: open.length },
            { value: 'all', label: 'All', count: s.alerts.length },
          ]}
        />
      }
      flush
    >
      {rows.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck />}
          title={view === 'open' && s.alerts.length ? 'No open alerts' : 'No alerts'}
          description="New networks, shared machine identities, command bursts and repeated refusals show up here."
        />
      ) : (
        <RowList label="Security alerts">
          {rows.map((a) => {
            const st = alertRowStatus(a);
            return (
              <li key={a.id} data-row="" className={cn('group relative flex min-h-row-2line items-center gap-3 px-row-x hover:bg-hover', openId === a.id && 'bg-active')}>
                <Pressable onClick={() => setOpenId(a.id)} aria-label={`${st.label}: ${a.title}. Open details`} className="absolute inset-0" />
                <span className="pointer-events-none shrink-0 lg:w-28">
                  <RowStatus {...st} hideLabel="below-lg" focusable={false} />
                </span>
                <span className="pointer-events-none flex min-w-0 flex-1 flex-col py-2">
                  <span className="line-clamp-2 text-ui text-fg sm:line-clamp-1">{a.title}</span>
                  <span className="flex min-w-0 items-center gap-3 text-caption text-fg-subtle">
                    {/* A phone shows only the icon in the status column: the severity word moves here. */}
                    <span className={cn('shrink-0 font-medium lg:hidden', a.status === 'acknowledged' ? 'text-fg-subtle' : a.severity === 'critical' ? 'text-status-danger' : 'text-status-attention')}>
                      {st.label}
                    </span>
                    {/* Two facts on a phone: the severity and the time; the machine is in the drawer. */}
                    <span data-meta="" className="min-w-0 truncate max-sm:hidden">
                      {a.machineId ? machineLabel(machines, a.machineId) : SECURITY_RULE_LABEL[a.rule].title}
                    </span>
                    <span data-meta="" className="shrink-0 num">
                      <span className="max-sm:hidden">{a.count > 1 ? `${formatInt(a.count)} times, last ` : ''}</span>
                      <RelativeTime ts={a.lastAt} />
                    </span>
                    {/* On a phone the action sits on the meta line, so the title keeps the row's width. */}
                    {a.status === 'open' && (
                      <Button size="sm" variant="ghost" className="pointer-events-auto relative -my-1 -mr-2 ml-auto shrink-0 sm:hidden" onClick={() => startAck(a)} aria-label={`Acknowledge ${a.title}`}>
                        Acknowledge
                      </Button>
                    )}
                  </span>
                </span>
                {a.status === 'open' && (
                  // Labelled at every width: a bare check at the row end reads as a done status.
                  <Button size="sm" variant="ghost" className="relative shrink-0 max-sm:hidden" onClick={() => startAck(a)} aria-label={`Acknowledge ${a.title}`}>
                    Acknowledge
                  </Button>
                )}
              </li>
            );
          })}
        </RowList>
      )}

      <Drawer
        open={!!shown}
        onOpenChange={(o) => !o && setOpenId(null)}
        width={480}
        layout="rows"
        eyebrow={shown && <RowStatus {...alertRowStatus(shown)} focusable={false} />}
        title={shown?.title ?? 'Alert'}
        description={
          shown && (
            <FactGrid
              label="Alert facts"
              lines={[
                [
                  { label: 'Machine', value: shown.machineId ? machineLabel(machines, shown.machineId) : undefined },
                  { label: 'Identity', value: shown.actor, title: shown.actor ?? undefined },
                  { label: 'First seen', value: <RelativeTime ts={shown.at} />, title: formatAbsolute(shown.at) },
                  { label: 'Occurrences', value: formatInt(shown.count) },
                ],
              ]}
            />
          )
        }
        footer={
          shown && (
            // One row: the audit link quiet at the left, the one strong action at the right. A phone
            // cannot fit three labelled buttons, so the audit link takes its own line under the actions,
            // still labelled: a bare icon there reads as decoration.
            <div className="flex w-full flex-wrap items-center justify-end gap-2">
              <Button variant="ghost" icon={<ScrollText />} onClick={() => navigate(auditLink(shown))} className="max-sm:order-last max-sm:w-full sm:mr-auto">
                Open audit log
              </Button>
              {shown.status === 'open' && (
                <Button variant={revokable(shown) ? 'ghost' : 'primary'} icon={<Check />} onClick={() => startAck(shown)}>
                  Acknowledge
                </Button>
              )}
              {revokable(shown) && (
                <Button variant="danger" icon={<ShieldOff />} onClick={() => setRevoke({ id: revokable(shown)!.id, name: revokable(shown)!.name })}>
                  Revoke {revokable(shown)!.name}
                </Button>
              )}
            </div>
          )
        }
      >
        {shown && (
          <div className="flex flex-col gap-6 px-6 py-4">
            <DetailSection title={SECURITY_RULE_LABEL[shown.rule].title}>
              <p className="text-ui text-fg">{shown.detail}</p>
            </DetailSection>
            {shown.status === 'open' ? (
              <DetailSection title="What to do">
                <p className="text-ui text-fg">{shown.next}</p>
              </DetailSection>
            ) : (
              shown.ack && (
                <DetailSection title="Acknowledged">
                  <p className="text-ui text-fg">
                    By {shown.ack.by} <RelativeTime ts={shown.ack.at} />
                    {shown.ack.note ? `: ${shown.ack.note}` : ''}
                  </p>
                </DetailSection>
              )
            )}
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={!!ack}
        onOpenChange={(o) => !o && setAck(null)}
        title={`Acknowledge "${ack?.title ?? ''}"?`}
        description="Your note goes to the audit log; nothing changes on any machine."
        confirmLabel="Acknowledge alert"
        tone="primary"
        onConfirm={async () => {
          if (ack) await sec.ack(ack.id, note.trim());
        }}
      >
        <Input label="Note" placeholder="For example: I moved the laptop to the office network" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} autoFocus />
      </ConfirmDialog>
      <RevokeConfirm target={revoke} onClose={() => setRevoke(null)} />
    </Panel>
  );
}

function RevokeConfirm({ target, onClose }: { target: { id: string; name: string } | null; onClose: () => void }) {
  return (
    <ConfirmDialog
      open={!!target}
      onOpenChange={(o) => !o && onClose()}
      title={`Revoke ${target?.name ?? ''}?`}
      description={target ? revokeConsequence(target.name) : ''}
      confirmLabel={`Revoke ${target?.name ?? 'machine'}`}
      onConfirm={async () => {
        if (!target) return;
        await revokeMachine(target.id);
        toast(`${target.name} revoked`, { tone: 'success', description: 'It cannot reconnect.' });
      }}
    />
  );
}

/** Freeze, pause a project and revoke a machine: one panel, one row each. */
function EmergencyControls({ t }: { t: TrustApi }) {
  if (t.loading && !t.state) {
    return (
      <Panel title="Emergency controls">
        <div role="status" aria-busy="true" aria-label="Loading kill switches">
          <SkeletonRows rows={3} />
        </div>
      </Panel>
    );
  }
  if (!t.state) {
    return (
      <Panel title="Emergency controls">
        <ErrorState variant="inline" error={t.error ?? new Error('The hub sent no trust state.')} onRetry={t.refresh} />
      </Panel>
    );
  }
  return (
    <KillSwitches t={t} s={t.state} title="Emergency controls">
      <RevokeRow />
    </KillSwitches>
  );
}

function RevokeRow() {
  const machines = useMachines();
  const [pick, setPick] = useState('');
  const [target, setTarget] = useState<{ id: string; name: string } | null>(null);
  const sorted = useMemo(() => [...machines].sort((a, b) => a.name.localeCompare(b.name)), [machines]);
  const chosen = sorted.find((m) => m.id === pick);
  return (
    <div className="flex flex-col gap-3 px-row-x py-3 md:flex-row md:items-center">
      <div className="min-w-0 flex-1">
        <p className="text-ui font-medium text-fg">Revoke a machine</p>
        <p className="text-caption text-fg-muted">Cuts off a lost or leaked machine at once, until it rejoins with a new join token.</p>
      </div>
      {sorted.length === 0 ? (
        <p className="text-caption text-fg-subtle">No machines are enrolled</p>
      ) : (
        <div className="flex items-center gap-2">
          <Select
            size="sm"
            aria-label="Machine to revoke"
            wrapperClassName="w-48"
            placeholder="Choose a machine"
            options={sorted.map((m) => ({ value: m.id, label: `${m.name}${m.online ? '' : ' (offline)'}` }))}
            value={pick}
            onValueChange={setPick}
          />
          <Button size="sm" variant="ghost" icon={<ShieldOff />} disabled={!chosen} onClick={() => chosen && setTarget({ id: chosen.id, name: chosen.name })}>
            Revoke
          </Button>
        </div>
      )}
      <RevokeConfirm
        target={target}
        onClose={() => {
          setTarget(null);
          setPick('');
        }}
      />
    </div>
  );
}

type Draft = DetectionSettings;

function Rules({ sec, s }: { sec: SecurityApi; s: SecurityState }) {
  const [draft, setDraft] = useState<Draft>(s.rules);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  useEffect(() => setDraft(s.rules), [s.rules]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(s.rules);
  const set = (id: SecurityRuleId, patch: Partial<Draft[SecurityRuleId]>) => setDraft((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  const num = (v: string) => Math.max(1, Math.floor(Number(v) || 1));
  const on = SECURITY_RULE_IDS.filter((id) => s.rules[id].enabled).length;
  const save = async () => {
    setSaving(true);
    setFailure(null);
    try {
      await sec.saveRules(draft);
      toast('Detection rules saved', { tone: 'success', description: 'Recorded in the audit log.' });
    } catch (e) {
      setFailure(e);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Panel
      id="security-rules"
      collapsible
      defaultOpen={dirty}
      title="Detection rules"
      sub={`${formatInt(on)} of ${formatInt(SECURITY_RULE_IDS.length)} on`}
      actions={
        dirty ? (
          <span className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setDraft(s.rules)}>
              Discard changes
            </Button>
            <Button size="sm" variant="primary" loading={saving} onClick={() => void save()}>
              Save rules
            </Button>
          </span>
        ) : undefined
      }
      flush
    >
      {failure != null && (
        <div className="border-b border-border-subtle px-row-x py-2">
          <ErrorState variant="inline" error={failure} onRetry={() => void save()} />
        </div>
      )}
      {/* Each rule reads as its threshold at rest; the switch turns it on or off, the row opens its numbers. */}
      <ul className="flex flex-col px-2 pb-2" aria-label="Detection rules">
        {SECURITY_RULE_IDS.map((id) => {
          const r = draft[id];
          const counted = id !== 'new-address';
          const unit = id === 'credential-two-places' ? 'addresses' : 'actions';
          const label = SECURITY_RULE_LABEL[id];
          return (
            <li key={id}>
              <Disclosure
                title={label.title}
                summary={!r.enabled ? 'Off' : counted ? `${formatInt(r.threshold)} ${unit} within ${formatInt(r.windowSec)} s` : 'On'}
                actions={<Switch checked={r.enabled} onCheckedChange={(v) => set(id, { enabled: v === true })} aria-label={`${label.title}: ${r.enabled ? 'on' : 'off'}`} />}
              >
                <div className="flex flex-col gap-3">
                  <p className="max-w-(--layout-width-prose) text-caption text-fg-subtle">{label.description}</p>
                  {counted && (
                    <div className="flex flex-wrap items-end gap-2 text-caption text-fg-subtle">
                      <Input
                        size="sm"
                        type="number"
                        min={1}
                        label={id === 'credential-two-places' ? 'Addresses' : 'Actions'}
                        value={r.threshold}
                        disabled={!r.enabled}
                        onChange={(e) => set(id, { threshold: num(e.target.value) })}
                        wrapperClassName="w-28"
                      />
                      <Input
                        size="sm"
                        type="number"
                        min={1}
                        label="Within"
                        value={r.windowSec}
                        disabled={!r.enabled}
                        onChange={(e) => set(id, { windowSec: num(e.target.value) })}
                        trailing={<span className="text-caption text-fg-subtle">s</span>}
                        wrapperClassName="w-28"
                      />
                    </div>
                  )}
                </div>
              </Disclosure>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

function CopyLine({ command, label }: { command: string; label: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'blocked'>('idle');
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-control border border-border-subtle bg-bg py-1 pl-3 pr-1">
      <code className="min-w-0 flex-1 select-all truncate font-mono text-ui text-fg">{command}</code>
      {state === 'blocked' && <span className="shrink-0 text-caption text-status-attention">Select it and copy by hand</span>}
      <IconButton
        label={state === 'copied' ? 'Copied' : label}
        icon={state === 'copied' ? <Check /> : <Copy />}
        size="sm"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(command);
            setState('copied');
            setTimeout(() => setState('idle'), 1500);
          } catch {
            setState('blocked');
          }
        }}
      />
    </div>
  );
}

function FactRow({ status, title, detail, children }: { status: Pick<RowStatusProps, 'tone' | 'icon' | 'label'>; title: ReactNode; detail?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 px-row-x py-3">
      <div className="flex min-w-0 items-start gap-3">
        <RowStatus {...status} hideLabel className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-ui font-medium text-fg">{title}</p>
          {detail != null && <p className="text-caption text-fg-muted">{detail}</p>}
        </div>
      </div>
      {children != null && <div className="flex flex-col gap-2 pl-7">{children}</div>}
    </div>
  );
}

function Backups({ s }: { s: SecurityState }) {
  const b = s.lastBackup;
  const stale = !b || Date.now() - b.at > WEEK;
  const behind = Object.entries(s.format.known).filter(([k, v]) => (s.format.versions[k] ?? 0) < v);
  const restore = s.lastRestore ? restoreSummary(s.lastRestore) : null;
  return (
    <Panel
      id="security-backups"
      title="Backups and hub folder"
      sub={`${!b ? 'No backup yet' : stale ? 'Last backup over a week old' : 'Backed up'}, format ${behind.length ? 'behind' : 'current'}`}
      collapsible
      defaultOpen={stale || behind.length > 0}
      flush
    >
      <div className="flex flex-col divide-y divide-border-subtle">
        <FactRow
          status={stale ? { tone: 'attention', icon: 'warning', label: 'Needs a backup' } : { tone: 'neutral', icon: 'done', label: 'Backed up' }}
          title={!b ? 'No backup yet' : stale ? 'Last backup is over a week old' : 'Backed up recently'}
          detail={
            b ? (
              <>
                {formatDate(b.at)}, {plural(b.files, 'file')}, {formatBytes(b.bytes)}, {b.mode === 'live' ? 'a live snapshot of the running hub' : 'taken while the hub was stopped'}
              </>
            ) : (
              'One encrypted file with machines, keys, the audit log and every module setting. Keep it off this machine.'
            )
          }
        >
          <CopyLine command="orgus backup" label="Copy the backup command" />
          <CopyLine command="orgus restore <file> --dry-run" label="Copy the restore check command" />
          <p className="text-caption text-fg-subtle">The passphrase never leaves your terminal; without it a backup cannot be restored. Steps: docs/runbooks/backup-restore.md.</p>
        </FactRow>
        {s.lastRestore && restore && (
          <FactRow
            status={restore.tone === 'waiting' ? { tone: 'attention', icon: 'warning', label: 'Check the restore' } : { tone: 'neutral', icon: 'done', label: 'Restored' }}
            title={
              <>
                {restore.title} <RelativeTime ts={s.lastRestore.at} />
              </>
            }
            detail={restore.detail}
          >
            <Link className="text-caption text-fg underline-offset-2 hover:underline" to="/audit?action=hub.restore">
              Open the restore entry in the audit log
            </Link>
          </FactRow>
        )}
        <FactRow
          status={behind.length ? { tone: 'attention', icon: 'warning', label: 'Format behind' } : { tone: 'neutral', icon: 'done', label: 'Format current' }}
          title={behind.length ? 'Folder format behind this version' : 'Folder format current'}
          detail={`${
            Object.entries(s.format.versions)
              .map(([k, v]) => `${k} ${v}`)
              .join(', ') || 'No format recorded yet'
          }. Migrations run when the hub starts, after a copy to format-backups/.`}
        >
          <p className="text-caption text-fg-subtle">
            Something looks wrong? <Link className="text-fg underline-offset-2 hover:underline" to="/audit">Verify the audit chain</Link> and follow docs/runbooks/incident-response.md.
          </p>
        </FactRow>
      </div>
    </Panel>
  );
}
